import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { API } from "@/App";
import sections from "@/data/sitemap_sections.json";

const COMPARE_PAGE_SIZE = 1000;
const slugify = (name) => name.toLowerCase().replace(/[.']/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

async function fetchPlayers() {
  const r = await axios.get(`${API}/fantasycalc/values?numQbs=2&numTeams=12&ppr=1`);
  return (r.data || [])
    .filter(pv => pv.player && ["QB", "RB", "WR", "TE"].includes(pv.player.position) && !/^\d{4}\s+Pick/.test(pv.player.name) && !pv.player.name.startsWith("20") && pv.value >= 100)
    .map(pv => ({ name: pv.player.name, slug: slugify(pv.player.name), position: pv.player.position, team: pv.player.maybeTeam || "FA", value: pv.value || 0 }));
}

function buildComparePairs(players) {
  const buckets = {};
  for (const p of players) { (buckets[p.position] = buckets[p.position] || []).push(p); }
  const seen = new Set();
  const list = [];
  for (const pos of ["QB", "RB", "WR", "TE"]) {
    const b = buckets[pos] || [];
    const cap = Math.min(b.length, 60);
    for (let i = 0; i < cap; i++) for (let j = i + 1; j < cap; j++) {
      const [s1, s2] = [b[i].slug, b[j].slug].sort();
      const key = `${s1}-vs-${s2}`;
      if (!seen.has(key)) { seen.add(key); list.push({ key, label: `${b[i].name} vs ${b[j].name}` }); }
    }
  }
  const top = players.slice(0, 15);
  for (let i = 0; i < top.length; i++) for (let j = i + 1; j < top.length; j++) {
    if (top[i].position === top[j].position) continue;
    const [s1, s2] = [top[i].slug, top[j].slug].sort();
    const key = `${s1}-vs-${s2}`;
    if (!seen.has(key)) { seen.add(key); list.push({ key, label: `${top[i].name} vs ${top[j].name}` }); }
  }
  return list;
}

function Shell({ title, description, path, crumb, children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title={title} description={description} path={path} />
      <PublicNav />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#ccff00] mb-2">SITEMAP</p>
        <nav className="text-xs text-[#888] mb-6">
          <Link to="/" className="hover:text-[#ccff00] no-underline text-[#888]">Home</Link>
          <span className="mx-1.5">/</span>
          <Link to="/sitemap" className="hover:text-[#ccff00] no-underline text-[#888]">Sitemap</Link>
          {crumb && <><span className="mx-1.5">/</span><span className="text-white">{crumb}</span></>}
        </nav>
        {children}
      </div>
    </div>
  );
}

const linkCls = "text-sm text-[#ccc] hover:text-[#ccff00] no-underline transition-colors";

export function SitemapIndex() {
  return (
    <Shell title="HTML Sitemap — Dynasty Trade Maker" description="Browse every page on Dynasty Trade Maker: tools, research articles, guides, player pages, trade pages, and comparisons." path="/sitemap">
      <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase mb-8">SITE <span className="text-[#ccff00]">MAP</span></h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 mb-10" data-testid="sitemap-index-sections">
        {sections.sections.map(s => (
          <div key={s.title}>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3 border-b border-[#262626] pb-2">{s.title}</h2>
            <ul className="space-y-1.5 list-none p-0 m-0">
              {s.links.map(l => <li key={l.url}><Link to={l.url} className={linkCls}>{l.name}</Link></li>)}
            </ul>
          </div>
        ))}
      </div>
      <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3 border-b border-[#262626] pb-2">Full Page Indexes</h2>
      <ul className="space-y-2 list-none p-0 m-0" data-testid="sitemap-subsitemap-links">
        <li><Link to="/sitemap/players" className={linkCls}>Player Pages Sitemap</Link> <span className="text-xs text-[#555]">— every dynasty player profile</span></li>
        <li><Link to="/sitemap/trades" className={linkCls}>Player Trade Pages Sitemap</Link> <span className="text-xs text-[#555]">— real trade history pages for the top 200 players</span></li>
        <li><Link to="/sitemap/compare/1" className={linkCls}>Player Comparison Pages Sitemap</Link> <span className="text-xs text-[#555]">— head-to-head comparison pages, 1,000 per page</span></li>
      </ul>
    </Shell>
  );
}

export function SitemapPlayers() {
  const [players, setPlayers] = useState(null);
  useEffect(() => { fetchPlayers().then(setPlayers).catch(() => setPlayers([])); }, []);
  return (
    <Shell title="Player Pages Sitemap — Dynasty Trade Maker" description="Index of every dynasty player profile page on Dynasty Trade Maker, grouped by position." path="/sitemap/players" crumb="Players">
      <h1 className="font-heading text-3xl text-white uppercase mb-6">PLAYER PAGES {players && <span className="text-[#666] text-lg">({players.length})</span>}</h1>
      {!players ? <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-6 skeleton rounded-sm w-1/2" />)}</div> : (
        ["QB", "RB", "WR", "TE"].map(pos => {
          const list = players.filter(p => p.position === pos);
          return (
            <div key={pos} className="mb-8">
              <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-3 border-b border-[#262626] pb-2">{pos} ({list.length})</h2>
              <ul className="columns-2 md:columns-3 lg:columns-4 gap-4 list-none p-0 m-0">
                {list.map(p => <li key={p.slug} className="mb-1.5"><Link to={`/players/${p.slug}`} className={linkCls}>{p.name}</Link></li>)}
              </ul>
            </div>
          );
        })
      )}
    </Shell>
  );
}

export function SitemapTrades() {
  const [players, setPlayers] = useState(null);
  useEffect(() => { fetchPlayers().then(p => setPlayers(p.slice(0, 200))).catch(() => setPlayers([])); }, []);
  return (
    <Shell title="Player Trade Pages Sitemap — Dynasty Trade Maker" description="Index of real dynasty trade history pages for the top 200 dynasty players." path="/sitemap/trades" crumb="Trades">
      <h1 className="font-heading text-3xl text-white uppercase mb-6">PLAYER TRADE PAGES {players && <span className="text-[#666] text-lg">({players.length})</span>}</h1>
      {!players ? <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-6 skeleton rounded-sm w-1/2" />)}</div> : (
        <ul className="columns-2 md:columns-3 lg:columns-4 gap-4 list-none p-0 m-0">
          {players.map(p => <li key={p.slug} className="mb-1.5"><Link to={`/trades/${p.slug}`} className={linkCls}>{p.name} Trades</Link></li>)}
        </ul>
      )}
    </Shell>
  );
}

export function SitemapCompare() {
  const { page } = useParams();
  const pageNum = Math.max(1, parseInt(page || "1", 10) || 1);
  const [pairs, setPairs] = useState(null);
  useEffect(() => { fetchPlayers().then(p => setPairs(buildComparePairs(p))).catch(() => setPairs([])); }, []);
  const totalPages = pairs ? Math.max(1, Math.ceil(pairs.length / COMPARE_PAGE_SIZE)) : 0;
  const slice = pairs ? pairs.slice((pageNum - 1) * COMPARE_PAGE_SIZE, pageNum * COMPARE_PAGE_SIZE) : [];
  return (
    <Shell title={`Player Comparison Pages Sitemap — Page ${pageNum} — Dynasty Trade Maker`} description={`Index of dynasty player head-to-head comparison pages, page ${pageNum}.`} path={`/sitemap/compare/${pageNum}`} crumb={`Comparisons — Page ${pageNum}`}>
      <h1 className="font-heading text-3xl text-white uppercase mb-2">COMPARISON PAGES {pairs && <span className="text-[#666] text-lg">({pairs.length.toLocaleString()} total)</span>}</h1>
      {totalPages > 0 && (
        <nav className="flex flex-wrap gap-2 mb-6" data-testid="sitemap-compare-pagination">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <Link key={n} to={`/sitemap/compare/${n}`} className={`px-3 py-1.5 text-xs font-bold rounded-sm border no-underline ${n === pageNum ? "border-[#ccff00] text-[#ccff00]" : "border-[#333] text-[#888] hover:text-white"}`}>Page {n}</Link>
          ))}
        </nav>
      )}
      {!pairs ? <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-6 skeleton rounded-sm w-1/2" />)}</div> : (
        <ul className="columns-1 md:columns-2 lg:columns-3 gap-4 list-none p-0 m-0">
          {slice.map(c => <li key={c.key} className="mb-1.5"><Link to={`/compare/${c.key}`} className={linkCls}>{c.label}</Link></li>)}
        </ul>
      )}
    </Shell>
  );
}
